import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { EditOutlined as EditIcon } from '@mui/icons-material';
import PersonService, { PersonDetails, PersonRecord, ServiceAreaOption } from '../../api/services/person.service';
import PayrollService, { describeError } from '../../api/services/payroll.service';
import { useAuthStore } from '../../store/authStore';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { NavPanel, PanelGroup, PanelLink } from '../../components/layout/NavPanel';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import { Avatar, Eyebrow } from '../payroll/payrollUI';
import { OUTCOME_LABEL, SOURCE_LABEL, STAGE_LABEL } from '../hiring/hiringUI';
import EditUserDialog from './EditUserDialog';
import PersonAccess from './PersonAccess';

// One person. Four records, one page.
//
// Who they are (User), their membership here (Account), how they are
// paid (PayProfile, AO/CA only) and how they were hired (Candidate) each
// live in their own collection and were each shown on a different screen,
// or on none. This page reads them together and edits the two that are
// this screen's to edit: identity and standing through the same dialog
// the list uses, and the membership details through a second one.

const fmtDate = (iso: string | null | undefined, empty = '—') => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : empty);
const toInputDate = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');

const Row: React.FC<{ label: string; children: React.ReactNode; tone?: 'warn' | 'danger' }> = ({ label, children, tone }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} sx={{ py: 0.75, borderBottom: '1px dashed', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" data-tone={tone} sx={{ fontWeight: 600, textAlign: 'right', minWidth: 0, color: tone === 'danger' ? 'error.dark' : tone === 'warn' ? 'warning.dark' : 'text.primary' }}>
      {children}
    </Typography>
  </Stack>
);

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Card sx={{ p: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
      <Eyebrow>{title}</Eyebrow>
      {action}
    </Stack>
    {children}
  </Card>
);

// A date that the compliance sweep reads. Past is a problem, soon is a warning.
const dateTone = (iso: string | null): 'warn' | 'danger' | undefined => {
  if (!iso) return undefined;
  const days = (new Date(iso).getTime() - Date.now()) / 86400000;
  if (days < 0) return 'danger';
  if (days < 30) return 'warn';
  return undefined;
};

const DetailsDialog: React.FC<{
  open: boolean;
  record: PersonRecord;
  areas: ServiceAreaOption[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, record, areas, onClose, onSaved }) => {
  const initial = useMemo(() => ({
    serviceAreaId: record.account.serviceAreaId == null ? '' : String(record.account.serviceAreaId),
    employeeId: record.account.employeeId,
    address: record.account.address,
    notes: record.account.notes,
    licenseExpiryDate: toInputDate(record.account.licenseExpiryDate),
    dotCardExpiryDate: toInputDate(record.account.dotCardExpiryDate),
    mecExpirationDate: toInputDate(record.account.mecExpirationDate),
  }), [record]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setForm(initial); setError(null); } }, [open, initial]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    const changes: PersonDetails = {};
    if (form.serviceAreaId !== initial.serviceAreaId) changes.serviceAreaId = form.serviceAreaId === '' ? null : Number(form.serviceAreaId);
    if (form.employeeId !== initial.employeeId) changes.employeeId = form.employeeId.trim();
    if (form.address !== initial.address) changes.address = form.address.trim();
    if (form.notes !== initial.notes) changes.notes = form.notes.trim();
    (['licenseExpiryDate', 'dotCardExpiryDate', 'mecExpirationDate'] as const).forEach((k) => {
      if (form[k] !== initial[k]) changes[k] = form[k] || null;
    });
    if (!Object.keys(changes).length) { onClose(); return; }
    setBusy(true);
    setError(null);
    try {
      await PersonService.updateDetails(record.accountId, changes);
      onSaved();
      onClose();
    } catch (err) {
      setError(describeError(err, 'That change was not saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Work details</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select size="small" label="Service area" value={areas.some((a) => String(a.serviceAreaId) === form.serviceAreaId) ? form.serviceAreaId : ''} onChange={set('serviceAreaId')}>
            <MenuItem value="">None</MenuItem>
            {areas.map((a) => <MenuItem key={a.serviceAreaId} value={String(a.serviceAreaId)}>{a.stationName || a.csa} · {a.csa}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Employee ID" value={form.employeeId} onChange={set('employeeId')} />
          <TextField size="small" label="Address" value={form.address} onChange={set('address')} />
          <Stack direction="row" gap={1}>
            <TextField size="small" label="License expires" type="date" value={form.licenseExpiryDate} onChange={set('licenseExpiryDate')} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField size="small" label="DOT card" type="date" value={form.dotCardExpiryDate} onChange={set('dotCardExpiryDate')} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <TextField size="small" label="Medical certificate expires" type="date" value={form.mecExpirationDate} onChange={set('mecExpirationDate')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Notes" value={form.notes} onChange={set('notes')} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const UserRecord: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { pathname } = useLocation();
  const { account: me } = useAuthStore();
  const [record, setRecord] = useState<PersonRecord | null>(null);
  const [areas, setAreas] = useState<ServiceAreaOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<'identity' | 'details' | null>(null);
  const [fedexId, setFedexId] = useState('');
  const [fedexBusy, setFedexBusy] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await PersonService.record(accountId);
      setRecord(r);
      setFedexId(r.pay?.fedexId || '');
    } catch (err) {
      setError(describeError(err, 'Could not load this person.'));
    } finally {
      setLoading(false);
    }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (editing !== 'details' || areas.length) return;
    PersonService.serviceAreas().then(setAreas).catch(() => setAreas([]));
  }, [editing, areas.length]);

  usePageBreadcrumb(record ? [{ label: 'Users', to: '/users' }, { label: record.user.name || record.user.email }] : []);

  const saveFedexId = async () => {
    if (!record || !record.pay || fedexId.trim() === record.pay.fedexId) return;
    setFedexBusy(true);
    setError(null);
    try {
      const saved = await PayrollService.saveProfile(record.accountId, { fedexId: fedexId.trim() });
      setRecord((r) => r && r.pay ? { ...r, pay: { ...r.pay, fedexId: saved.fedexId, profileId: saved.profileId, description: saved.description, hasRules: saved.hasRules, rules: saved.rules } } : r);
      setNotice('FedEx ID saved.');
    } catch (err) {
      setError(describeError(err, 'That FedEx ID was not saved.'));
      setFedexId(record.pay.fedexId);
    } finally {
      setFedexBusy(false);
    }
  };

  const panel = (
    <NavPanel>
      <PanelGroup label="People">
        <PanelLink to="/users" label="All users" currentPath={pathname} />
        <PanelLink to="/hiring" label="Hiring" currentPath={pathname} />
        <PanelLink to="/payroll/people" label="Driver pay" currentPath={pathname} />
      </PanelGroup>
    </NavPanel>
  );

  if (loading && !record) return <PageBody>{panel}<LoadingState /></PageBody>;
  if (!record) {
    return (
      <PageBody>
        {panel}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <EmptyState title="Nobody here" description="This person could not be found in your company." />
      </PageBody>
    );
  }

  const { user, account, pay, hiring } = record;
  const statusTone = account.status === 'active' ? 'ok' : account.status === 'pending' ? 'warn' : 'neutral';
  const editRow = { _id: record.accountId, firstName: user.firstName, lastName: user.lastName, email: user.email, phone: user.phone, role: account.role, status: account.status };

  return (
    <PageBody>
      {panel}
      <PageHeader
        title={(
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Avatar name={user.name || user.email} size={44} />
            <Box>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <span>{user.name || user.email}</span>
                <Chip label={account.role} size="small" variant="outlined" />
                {account.alsoPlatformAdmin && account.role !== 'SA' && (
                  <Tooltip title="Also the platform administrator"><Chip label="SA" size="small" color="primary" variant="outlined" /></Tooltip>
                )}
                <ToneChip tone={statusTone} label={account.status} hollow={account.status === 'pending' || account.status === 'former'} />
              </Stack>
            </Box>
          </Stack>
        )}
        subtitle={[
          account.serviceArea ? `${account.serviceArea.stationName || account.serviceArea.csa}` : null,
          account.employeeId ? `Employee ${account.employeeId}` : null,
          account.createdAt ? `Joined ${fmtDate(account.createdAt)}` : null,
        ].filter(Boolean).join(' · ') || undefined}
        actions={<Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing('identity')}>Edit</Button>}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
        <Section title="Contact">
          <Row label="Email">{user.email ? <Link href={`mailto:${user.email}`}>{user.email}</Link> : '—'}</Row>
          <Row label="Phone">{formatPhoneNumber(user.phone) || '—'}</Row>
          <Row label="Address">{account.address || '—'}</Row>
          <Row label="Invitation">{account.invitationAcceptedAt ? `Accepted ${fmtDate(account.invitationAcceptedAt)}` : account.invitationSentAt ? `Sent ${fmtDate(account.invitationSentAt)}, not yet accepted` : '—'}</Row>
          <Row label="Last seen">{fmtDate(account.lastAccessedAt, 'Never')}</Row>
        </Section>

        <Section
          title="Work"
          action={<Button size="small" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={() => setEditing('details')} sx={{ color: 'text.secondary', py: 0 }}>Edit</Button>}
        >
          <Row label="Service area">{account.serviceArea ? `${account.serviceArea.stationName || ''} ${account.serviceArea.csa ? `(${account.serviceArea.csa})` : ''}`.trim() : account.serviceAreaId != null ? String(account.serviceAreaId) : '—'}</Row>
          <Row label="Employee ID">{account.employeeId || '—'}</Row>
          <Row label="Manager">{account.managerName || '—'}</Row>
          <Row label="License expires" tone={dateTone(account.licenseExpiryDate)}>{fmtDate(account.licenseExpiryDate)}</Row>
          <Row label="DOT card expires" tone={dateTone(account.dotCardExpiryDate)}>{fmtDate(account.dotCardExpiryDate)}</Row>
          <Row label="Medical certificate" tone={dateTone(account.mecExpirationDate)}>{fmtDate(account.mecExpirationDate)}</Row>
          {account.notes && <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>{account.notes}</Typography>}
        </Section>

        {pay && (
          <Section
            title="Pay"
            action={<Button size="small" component={RouterLink} to={`/payroll/people/${record.accountId}`} sx={{ py: 0 }}>Pay rules</Button>}
          >
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="FedEx ID"
                value={fedexId}
                disabled={fedexBusy}
                onChange={(e) => setFedexId(e.target.value)}
                onBlur={saveFedexId}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'FedEx ID', style: { fontVariantNumeric: 'tabular-nums' } }}
                sx={{ width: 180 }}
              />
              <Typography variant="caption" color="text.secondary">What the weekly statement calls them.</Typography>
            </Stack>
            <Row label="Rules">
              {pay.hasRules ? pay.description : pay.profileId ? <Box component="span" sx={{ color: 'warning.dark' }}>Rules without amounts yet</Box> : <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>Not set up</Box>}
            </Row>
          </Section>
        )}

        <Section
          title="Hiring"
          action={hiring ? <Button size="small" component={RouterLink} to={`/hiring/${hiring.candidateId}`} sx={{ py: 0 }}>Candidate record</Button> : undefined}
        >
          {hiring ? (
            <>
              <Row label="Outcome">{hiring.outcome ? OUTCOME_LABEL[hiring.outcome as keyof typeof OUTCOME_LABEL] || hiring.outcome : STAGE_LABEL[hiring.stage as keyof typeof STAGE_LABEL] || hiring.stage}{hiring.hiredAt ? `, ${fmtDate(hiring.hiredAt)}` : ''}</Row>
              <Row label="Source">{SOURCE_LABEL[hiring.source as keyof typeof SOURCE_LABEL] || hiring.source}{hiring.sourceDetail ? ` · ${hiring.sourceDetail}` : ''}</Row>
              <Row label="Offered">{hiring.offeredPayRate != null ? `$${hiring.offeredPayRate}` : '—'}</Row>
              <Row label="Start date">{fmtDate(hiring.targetStartDate)}</Row>
              <Row label="License">{hiring.licenseNumber ? `${hiring.licenseNumber}${hiring.licenseState ? ` (${hiring.licenseState})` : ''}` : '—'}</Row>
              <Row label="Uniform">{hiring.uniformSizes || '—'}</Row>
              <Row label="Work authorized">{hiring.workAuthorized === null ? '—' : hiring.workAuthorized ? 'Yes' : 'No'}</Row>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">No candidate record. They joined before hiring moved here, or were invited directly.</Typography>
          )}
        </Section>
        <PersonAccess accountId={record.accountId} role={account.role} isSelf={record.accountId === me?._id} />
      </Box>

      <EditUserDialog
        open={editing === 'identity'}
        user={editRow}
        callerRole={me?.role || ''}
        onClose={() => setEditing(null)}
        onSaved={(message) => { setNotice(message); load(); }}
      />
      <DetailsDialog
        open={editing === 'details'}
        record={record}
        areas={areas}
        onClose={() => setEditing(null)}
        onSaved={() => { setNotice('Saved.'); load(); }}
      />
    </PageBody>
  );
};

export default UserRecord;
